import { useId, useRef, useState } from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import CalendarIcon from '../../../assets/Svg/CalendarIcon';
import FirstYearIcon from '../../../assets/Svg/FirstYearIcon';
import LastYearIcon from '../../../assets/Svg/LastYearIcon';
import { monthsEnum } from '../../../utils/dateUtils';
import { useOutsideClick } from '../../../utils/constants/hooks';
import styles from './MonthYearPicker.module.scss';

// Object.values(monthsEnum) can't be used directly here — '10'/'11'/'12' are
// canonical integer-like keys, so JS engines reorder them ahead of '01'-'09'
// regardless of insertion order. Look each month up by its zero-padded key instead.
const MONTH_LABELS = Array.from({ length: 12 }, (_, index) => monthsEnum[String(index + 1).padStart(2, '0')].name);

interface MonthYearPickerProps {
    /** Selected month, 'YYYY-MM'. */
    value: string;
    /** Inclusive lower bound, 'YYYY-MM'. */
    min?: string;
    /** Inclusive upper bound, 'YYYY-MM'. */
    max?: string;
    onChange: (month: string) => void;
    className?: string;
}

const toMonthKey = (year: number, monthIndex: number) => `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

function MonthYearPicker({ value, min, max, onChange, className }: MonthYearPickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() => (value ? Number(value.slice(0, 4)) : dayjs().year()));
    const containerRef = useRef<HTMLDivElement>(null);
    const popupId = useId();

    useOutsideClick(containerRef, () => {
        if (isOpen) setIsOpen(false);
    });

    const openPopup = () => {
        setViewYear(value ? Number(value.slice(0, 4)) : dayjs().year());
        setIsOpen(true);
    };

    const isMonthDisabled = (monthKey: string) => (!!min && monthKey < min) || (!!max && monthKey > max);

    const handleSelect = (monthIndex: number) => {
        const monthKey = toMonthKey(viewYear, monthIndex);
        if (isMonthDisabled(monthKey)) return;
        onChange(monthKey);
        setIsOpen(false);
    };

    // Placeholder wording is a RECUR-27384 assumption (flagged) — Figma dev-mode
    // spec doesn't dictate literal copy for the empty state, "Select" is the
    // closest existing convention (see the pre-existing fallback below).
    const isEmpty = !value;
    const label = value ? dayjs(`${value}-01`).format('MMM-YYYY') : 'Select';
    // Applied = has a value and isn't the ALL/empty state; the chip visually
    // distinguishes DEFAULT (white, grey border) / OPEN (purple border) / APPLIED
    // (purple bg, no border) per Figma — isOpen wins over APPLIED while the popup
    // is showing, matching the spec's "border becomes purple on that chip".
    const chipState = isOpen ? 'open' : !isEmpty ? 'applied' : 'default';

    return (
        <div className={clsx(styles.MonthYearPicker, className)} ref={containerRef}>
            <button
                type="button"
                className={clsx(styles.Chip, {
                    [styles.ChipOpen]: chipState === 'open',
                    [styles.ChipApplied]: chipState === 'applied',
                })}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={isOpen ? popupId : undefined}
                onClick={() => (isOpen ? setIsOpen(false) : openPopup())}
            >
                <span className={clsx(styles.ChipLabel, { [styles.ChipPlaceholder]: isEmpty })}>{label}</span>
                {/* Same glyph + state colours as the Reconciliation page's DatePicker
                    (design-alignment request): grey at rest, purple when open/applied. */}
                <CalendarIcon
                    className={styles.CalendarIcon}
                    stroke={chipState === 'default' ? '#4D4D4D' : 'var(--primary-purple)'}
                    strokeWidth={2}
                />
            </button>
            {isOpen && (
                <div id={popupId} className={styles.Popup} role="dialog" aria-label="Select month and year">
                    <div className={styles.PopupHeader}>
                        <button
                            type="button"
                            className={styles.YearNavButton}
                            onClick={() => setViewYear((y) => y - 1)}
                            aria-label="Previous year"
                        >
                            <FirstYearIcon />
                        </button>
                        <span className={styles.YearLabel}>{viewYear}</span>
                        <button
                            type="button"
                            className={styles.YearNavButton}
                            onClick={() => setViewYear((y) => y + 1)}
                            aria-label="Next year"
                        >
                            <LastYearIcon />
                        </button>
                    </div>
                    <div className={styles.MonthGrid}>
                        {MONTH_LABELS.map((monthLabel, index) => {
                            const monthKey = toMonthKey(viewYear, index);
                            const disabled = isMonthDisabled(monthKey);
                            const selected = monthKey === value;
                            return (
                                <button
                                    type="button"
                                    key={monthLabel}
                                    className={clsx(styles.MonthCell, { [styles.Selected]: selected, [styles.Disabled]: disabled })}
                                    disabled={disabled}
                                    onClick={() => handleSelect(index)}
                                >
                                    {monthLabel}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default MonthYearPicker;
